import Swal from "sweetalert2"

export type ConfirmationType = "danger" | "warning" | "info"

export interface ConfirmationOptions {
  text: string
  title?: string
  confirmButtonText?: string
  cancelButtonText?: string
  type?: ConfirmationType
}

const CUSTOM_CLASSES: Record<ConfirmationType, {
  confirmButton: string
  cancelButton: string
  icon: string
}> = {
  danger: {
    confirmButton: "btn fw-bold btn-danger btn-active",
    cancelButton: "swal2-cancel-link text-body-secondary text-decoration-none fw-medium",
    icon: "swal2-icon-danger"
  },
  warning: {
    confirmButton: "btn fw-bold btn-warning btn-active",
    cancelButton: "swal2-cancel-link text-body-secondary text-decoration-none fw-medium",
    icon: "swal2-icon-warning"
  },
  info: {
    confirmButton: "btn fw-bold btn-primary btn-active",
    cancelButton: "swal2-cancel-link text-body-secondary text-decoration-none fw-medium",
    icon: "swal2-icon-info"
  }
}

const ICON_MAP: Record<ConfirmationType, "warning" | "question"> = {
  danger: "warning",
  warning: "warning",
  info: "question"
}

/**
 * Standardized confirmation dialog utility for SAUL layout.
 * Wraps SweetAlert2 with consistent styling and common patterns.
 *
 * Usage:
 *   // Simple delete confirmation
 *   if (await Confirmation.confirmDelete("this client")) { ... }
 *
 *   // Bulk delete
 *   if (await Confirmation.confirmBulkDelete("clients")) { ... }
 *
 *   // Custom confirmation
 *   if (await Confirmation.confirm({
 *     title: "Close Account?",
 *     text: "Your subscription will be canceled...",
 *     confirmButtonText: "Yes, close account",
 *     type: "danger"
 *   })) { ... }
 */
export default class Confirmation {
  /**
   * Show a confirmation dialog with custom options.
   */
  static async confirm(options: ConfirmationOptions): Promise<boolean> {
    const type = options.type || "danger"

    const result = await Swal.fire({
      title: options.title,
      text: options.text,
      icon: ICON_MAP[type],
      showCancelButton: true,
      buttonsStyling: false,
      confirmButtonText: options.confirmButtonText || "Yes",
      cancelButtonText: options.cancelButtonText || "No, cancel",
      customClass: CUSTOM_CLASSES[type]
    })

    return result.isConfirmed
  }

  /**
   * Confirmation for deleting a single item.
   * @param itemName - Name or description of the item (e.g., "this client", "John Doe")
   */
  static async confirmDelete(itemName: string): Promise<boolean> {
    return this.confirm({
      text: `Are you sure you want to delete ${itemName}?`,
      confirmButtonText: "Yes, delete!",
      type: "danger"
    })
  }

  /**
   * Confirmation for archiving a single item.
   * @param itemName - Optional name of the item. If omitted, uses "this".
   */
  static async confirmArchive(itemName?: string): Promise<boolean> {
    const text = itemName
      ? `Are you sure you want to archive ${itemName}?`
      : "Are you sure you want to archive this?"

    return this.confirm({
      text,
      confirmButtonText: "Yes, archive!",
      type: "warning"
    })
  }

  /**
   * Confirmation for recovering a single archived item.
   * @param itemName - Optional name of the item. If omitted, uses "this".
   */
  static async confirmRecover(itemName?: string): Promise<boolean> {
    const text = itemName
      ? `Are you sure you want to recover ${itemName}?`
      : "Are you sure you want to recover this?"

    return this.confirm({
      text,
      confirmButtonText: "Yes, recover!",
      type: "info"
    })
  }

  /**
   * Confirmation for removing a single item (e.g., from a group).
   * @param itemName - Name or description of the item
   */
  static async confirmRemove(itemName: string): Promise<boolean> {
    return this.confirm({
      text: `Are you sure you want to remove ${itemName}?`,
      confirmButtonText: "Yes, remove!",
      type: "danger"
    })
  }

  /**
   * Confirmation for deleting multiple selected items.
   * @param itemType - Plural type of items (e.g., "clients", "groups")
   */
  static async confirmBulkDelete(itemType: string): Promise<boolean> {
    return this.confirm({
      text: `Are you sure you want to delete the selected ${itemType}?`,
      confirmButtonText: "Yes, delete!",
      type: "danger"
    })
  }

  /**
   * Confirmation for archiving multiple selected items.
   * @param itemType - Plural type of items (e.g., "responses", "messages")
   */
  static async confirmBulkArchive(itemType: string): Promise<boolean> {
    return this.confirm({
      text: `Are you sure you want to archive the selected ${itemType}?`,
      confirmButtonText: "Yes, archive!",
      type: "warning"
    })
  }

  /**
   * Confirmation for removing multiple selected items.
   * @param itemType - Plural type of items (e.g., "clients")
   */
  static async confirmBulkRemove(itemType: string): Promise<boolean> {
    return this.confirm({
      text: `Are you sure you want to remove the selected ${itemType}?`,
      confirmButtonText: "Yes, remove!",
      type: "danger"
    })
  }

  /**
   * Confirmation for canceling a scheduled item.
   * @param itemName - Name or description of the item (e.g., "this scheduled message")
   */
  static async confirmCancel(itemName: string): Promise<boolean> {
    return this.confirm({
      text: `Are you sure you want to cancel ${itemName}?`,
      confirmButtonText: "Yes, cancel it!",
      cancelButtonText: "No, keep it",
      type: "danger"
    })
  }
}
